import React, { useState, useEffect } from 'react';
import SoftGrid from '../Shared/SoftGrid/SoftGrid';
import ApiServices from '../Services/ApiServices';
import { toastHelper } from '../../Helpers/toastHelper';
import { getFullFileUrl } from '../../Helpers/UrlHelpers';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faPlus,
    faPencilAlt,
    faTrash,
    faEye,
    faEyeSlash,
    faSort,
    faArrowUp,
    faArrowDown,
    faSave,
    faTimes,
    faChalkboardTeacher,
    faInfoCircle,
    faMapMarkerAlt,
    faStar,
    faCheckCircle
} from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import DeleteAlert from '../Shared/DeleteAlert';
import Select from 'react-select';

const AdsViewer = () => {
    const navigate = useNavigate();
    const [ads, setAds] = useState([]);
    const [loading, setLoading] = useState(true);
    const [teachers, setTeachers] = useState([]);
    const [selectedTeacher, setSelectedTeacher] = useState(null);

    // Reorder Mode
    const [reorderMode, setReorderMode] = useState(false);
    const [reorderedAds, setReorderedAds] = useState([]);
    const [submittingOrder, setSubmittingOrder] = useState(false);

    // Delete Logic
    const [deleteDialog, setDeleteDialog] = useState({ open: false, ad: null });

    const fetchAds = async () => {
        setLoading(true);
        try {
            const params = { IsActive: true };
            if (selectedTeacher) params.TeacherId = selectedTeacher.value;

            const response = await ApiServices.get('AdsViewer/getAll', params);
            let data = [];
            if (response && response.data && response.data.items) {
                data = response.data.items;
            } else if (Array.isArray(response)) {
                data = response;
            } else if (response && response.data) {
                data = response.data;
            }
            setAds(data || []);
        } catch (error) {
            toastHelper.error('فشل في تحميل الإعلانات');
        } finally {
            setLoading(false);
        }
    };

    const fetchTeachers = async () => {
        try {
            const res = await ApiServices.get('Lookup/Teachers');
            if (res && Array.isArray(res)) {
                setTeachers(res.map(t => ({ value: t.id, label: t.name })));
            }
        } catch (e) { }
    };

    useEffect(() => {
        fetchAds();
        fetchTeachers();
    }, [selectedTeacher]);

    const handleDelete = async () => {
        try {
            setLoading(true);
            const adId = deleteDialog.ad.id || deleteDialog.ad.ads_id;
            const res = await ApiServices.delete(`AdsViewer/delete/${adId}`);
            if (res && res.check) {
                toastHelper.success('تم حذف الإعلان بنجاح');
                fetchAds();
            }
        } catch (error) {
            toastHelper.error('فشل في حذف الإعلان');
        } finally {
            setLoading(false);
            setDeleteDialog({ open: false, ad: null });
        }
    };

    const handleToggleHide = async (ad) => {
        try {
            setLoading(true);
            const adId = ad.id || ad.ads_id;
            const res = await ApiServices.put(`AdsViewer/showOrHide/${adId}`);
            if (res && res.check) {
                toastHelper.success('تم تحديث حالة الظهور');
                fetchAds();
            }
        } catch (error) {
            toastHelper.error('فشل في تحديث الحالة');
        } finally {
            setLoading(false);
        }
    };

    const startReorder = () => {
        setReorderMode(true);
        setReorderedAds([...ads]);
    };

    const moveAd = (index, direction) => {
        const newArr = [...reorderedAds];
        const targetIndex = direction === 'up' ? index - 1 : index + 1;
        if (targetIndex < 0 || targetIndex >= newArr.length) return;

        const temp = newArr[index];
        newArr[index] = newArr[targetIndex];
        newArr[targetIndex] = temp;
        setReorderedAds(newArr);
    };

    const saveOrder = async () => {
        setSubmittingOrder(true);
        try {
            const orderData = reorderedAds.map(ad => ad.id || ad.ads_id);
            const res = await ApiServices.put('AdsViewer/updateOrder', orderData);
            if (res && res.check) {
                toastHelper.success('تم تحديث ترتيب الإعلانات');
                setReorderMode(false);
                fetchAds();
            }
        } catch (error) {
            toastHelper.error('فشل في حفظ الترتيب');
        } finally {
            setSubmittingOrder(false);
        }
    };

    const columns = [
        {
            key: 'image',
            label: 'الصورة',
            width: '100px',
            render: (val) => <img src={getFullFileUrl(val)} className="ad-thumb" alt="Ad" />
        },
        {
            key: 'title',
            label: 'العنوان',
            sortable: true,
            render: (val, row) => (
                <div className="title-cell">
                    <span className="ad-title-txt">{val || 'بدون عنوان'}</span>
                    {row.is_platform_owner && <span className="platform-tag">المنصة</span>}
                </div>
            )
        },
        {
            key: 'teacher_name',
            label: 'المعلم',
            render: (val) => val ? <span><FontAwesomeIcon icon={faChalkboardTeacher} /> {val}</span> : '-'
        },
        {
            key: 'price',
            label: 'السعر',
            render: (val, row) => (
                <div className="price-cell">
                    <span className="main-price">{val || 0} ج.م</span>
                    {row.discount > 0 && <span className="discount-badge">-{row.discount}%</span>}
                </div>
            )
        },
        {
            key: 'stats',
            label: 'بيانات',
            render: (_, row) => (
                <div className="ad-meta">
                    <span title="الطلاب"><FontAwesomeIcon icon={faStar} /> {row.rating || 0}</span>
                    <span title="الموقع"><FontAwesomeIcon icon={faMapMarkerAlt} /> {row.location || '-'}</span>
                </div>
            )
        },
        {
            key: 'added_date',
            label: 'تاريخ الإضافة',
            sortable: true,
            render: (val) => val ? new Date(val).toLocaleDateString('ar-EG') : '-'
        },
        {
            key: 'status',
            label: 'الحالة',
            render: (val, row) => (
                <span className={`status-pill ${row.is_hidden ? 'hidden' : 'visible'}`}>
                    <FontAwesomeIcon icon={row.is_hidden ? faEyeSlash : faEye} />
                    {row.is_hidden ? 'مخفي' : 'ظاهر'}
                </span>
            )
        }
    ];

    const actions = (row) => [
        {
            label: 'تعديل',
            icon: <FontAwesomeIcon icon={faPencilAlt} />,
            onClick: (row) => navigate('/admin/ads-viewer/edit', { state: { ad: row } }),
            className: 'edit-btn'
        },
        {
            label: row.is_hidden ? 'إظهار' : 'إخفاء',
            icon: <FontAwesomeIcon icon={row.is_hidden ? faEye : faEyeSlash} />,
            onClick: (row) => handleToggleHide(row),
            className: 'toggle-btn'
        },
        {
            label: 'حذف',
            icon: <FontAwesomeIcon icon={faTrash} />,
            onClick: (row) => setDeleteDialog({ open: true, ad: row }),
            className: 'delete-btn'
        }
    ];

    if (reorderMode) {
        return (
            <div className="ads-reorder-container">
                <header className="reorder-header">
                    <h2>إعادة ترتيب الإعلانات</h2>
                    <div className="reorder-btns">
                        <button className="save-order-btn" onClick={saveOrder} disabled={submittingOrder}>
                            <FontAwesomeIcon icon={faSave} spin={submittingOrder} />
                            <span>حفظ الترتيب</span>
                        </button>
                        <button className="cancel-reorder-btn" onClick={() => setReorderMode(false)}>
                            <FontAwesomeIcon icon={faTimes} />
                            <span>إلغاء</span>
                        </button>
                    </div>
                </header>
                <div className="reorder-list">
                    {reorderedAds.map((ad, idx) => (
                        <div key={ad.id || ad.ads_id} className="reorder-item">
                            <span className="order-num">{idx + 1}</span>
                            <img src={getFullFileUrl(ad.image)} alt="" className="reorder-img" />
                            <div className="reorder-info">
                                <h3>{ad.title}</h3>
                                <p>{ad.teacher_name || 'بدون معلم'}</p>
                            </div>
                            <div className="reorder-actions">
                                <button disabled={idx === 0} onClick={() => moveAd(idx, 'up')}>
                                    <FontAwesomeIcon icon={faArrowUp} />
                                </button>
                                <button disabled={idx === reorderedAds.length - 1} onClick={() => moveAd(idx, 'down')}>
                                    <FontAwesomeIcon icon={faArrowDown} />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
                <style>{`
                    .ads-reorder-container { padding: 30px; direction: rtl; }
                    .reorder-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 30px; }
                    .reorder-btns { display: flex; gap: 15px; }
                    .save-order-btn { background: #10b981; color: white; border: none; padding: 10px 25px; border-radius: 10px; cursor: pointer; display: flex; align-items: center; gap: 10px; font-weight: 700; }
                    .cancel-reorder-btn { background: #f1f5f9; color: #64748b; border: none; padding: 10px 25px; border-radius: 10px; cursor: pointer; font-weight: 700; }
                    
                    .reorder-list { display: flex; flex-direction: column; gap: 15px; max-width: 800px; margin: 0 auto; }
                    .reorder-item { display: flex; align-items: center; gap: 20px; background: white; padding: 15px; border-radius: 15px; box-shadow: 0 4px 6px rgba(0,0,0,0.05); }
                    .order-num { font-weight: 800; font-size: 1.2rem; color: #6366f1; width: 30px; }
                    .reorder-img { width: 80px; height: 50px; object-fit: cover; border-radius: 8px; }
                    .reorder-info { flex: 1; }
                    .reorder-info h3 { margin: 0; font-size: 1rem; color: #1e293b; }
                    .reorder-info p { margin: 2px 0 0; font-size: 0.85rem; color: #64748b; }
                    .reorder-actions { display: flex; gap: 10px; }
                    .reorder-actions button { width: 40px; height: 40px; border-radius: 10px; border: 1px solid #e2e8f0; background: white; cursor: pointer; color: #6366f1; transition: all 0.2s; }
                    .reorder-actions button:hover:not(:disabled) { background: #6366f1; color: white; }
                    .reorder-actions button:disabled { opacity: 0.3; cursor: not-allowed; }
                `}</style>
            </div>
        );
    }

    return (
        <div className="ads-viewer-container">
            <div className="page-header">
                <div className="header-info">
                    <h2>إدارة الإعلانات (Ads Viewer)</h2>
                    <p>إدارة الإعلانات المعروضة في التطبيق وترتيبها</p>
                </div>
                <div className="header-actions">
                    <button className="reorder-btn" onClick={startReorder}>
                        <FontAwesomeIcon icon={faSort} />
                        <span>إعادة الترتيب</span>
                    </button>
                    <button className="add-ad-btn" onClick={() => navigate('/admin/ads-viewer/add')}>
                        <FontAwesomeIcon icon={faPlus} />
                        <span>إضافة إعلان جديد</span>
                    </button>
                </div>
            </div>

            <div className="filters-section">
                <div className="filter-group">
                    <label>تصفية حسب المعلم:</label>
                    <Select
                        options={[{ value: null, label: 'الكل (بدون تصفية)' }, ...teachers]}
                        isClearable
                        placeholder="اختر المعلم..."
                        onChange={(opt) => setSelectedTeacher(opt)}
                        value={selectedTeacher}
                        className="teacher-select"
                        styles={{
                            control: (base) => ({ ...base, borderRadius: '12px', border: '2px solid #e2e8f0' })
                        }}
                    />
                </div>
            </div>

            <SoftGrid
                columns={columns}
                data={ads}
                isLoading={loading}
                actions={actions}
                searchPlaceholder="ابحث في العناوين أو الوصف..."
            />

            <DeleteAlert
                isOpen={deleteDialog.open}
                onClose={() => setDeleteDialog({ open: false, ad: null })}
                onConfirm={handleDelete}
                itemName={`الإعلان: ${deleteDialog.ad?.title}`}
                loading={loading}
            />

            <style>{`
                .ads-viewer-container { padding: 20px; direction: rtl; }
                .page-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 24px; }
                .header-info h2 { margin: 0; color: #1e293b; font-size: 1.5rem; font-weight: 800; }
                .header-info p { margin: 4px 0 0; color: #64748b; }
                
                .header-actions { display: flex; gap: 15px; }
                .add-ad-btn { background: #6366f1; color: white; border: none; padding: 12px 24px; border-radius: 12px; display: flex; align-items: center; gap: 10px; font-weight: 700; cursor: pointer; box-shadow: 0 4px 6px -1px rgba(99, 102, 241, 0.4); }
                .reorder-btn { background: white; color: #6366f1; border: 2px solid #6366f1; padding: 10px 20px; border-radius: 12px; display: flex; align-items: center; gap: 10px; font-weight: 700; cursor: pointer; }
                
                .filters-section { margin-bottom: 20px; background: white; padding: 15px; border-radius: 15px; border: 1px solid #e2e8f0; }
                .filter-group { display: flex; align-items: center; gap: 15px; max-width: 400px; }
                .filter-group label { font-weight: 700; color: #475569; white-space: nowrap; }
                .teacher-select { flex: 1; }

                .ad-thumb { width: 80px; height: 50px; object-fit: cover; border-radius: 8px; border: 1px solid #f1f5f9; }
                .title-cell { display: flex; flex-direction: column; gap: 4px; }
                .ad-title-txt { font-weight: 700; color: #1e293b; }
                .platform-tag { background: #e0f2fe; color: #0369a1; font-size: 0.65rem; padding: 2px 8px; border-radius: 4px; font-weight: 800; width: fit-content; }
                
                .price-cell { display: flex; align-items: center; gap: 8px; }
                .main-price { font-weight: 800; color: #10b981; }
                .discount-badge { background: #fee2e2; color: #ef4444; font-size: 0.7rem; padding: 2px 6px; border-radius: 4px; font-weight: 700; }
                
                .ad-meta { display: flex; gap: 10px; font-size: 0.75rem; color: #64748b; }
                .ad-meta span { display: flex; align-items: center; gap: 4px; }
                
                .status-pill { display: flex; align-items: center; gap: 6px; font-size: 0.8rem; font-weight: 700; padding: 5px 12px; border-radius: 20px; width: fit-content; }
                .status-pill.visible { background: #dcfce7; color: #16a34a; }
                .status-pill.hidden { background: #f1f5f9; color: #94a3b8; }
            `}</style>
        </div>
    );
};

export default AdsViewer;
